import { useState } from "react";

import { useParams } from "react-router";
import { type VariableValue } from "@doitintl/cmp-models";
import { Stack, Typography } from "@mui/material";

import { cloudflowTexts } from "../../../../../assets/texts";
import DeleteDialog from "../../../../../Components/DeleteDialog";
import { SideDrawer } from "../../../../../Components/SideDrawer";
import Tabs, { type TabConfig } from "../../../../../Components/Tabs";
import { useLocalVariables } from "../../../GlobalVariables/VariableManager";
import { type VariableScope } from "../../../types";
import { useModalManager } from "../../Common/CloudflowModalsProvider";
import CreateVariableModalWrapper from "../../Common/CreateVariableModalWrapper";
import { useVariableContext } from "../../Common/VariableProvider";
import VariableTab from "./VariableTab";

const VariableConfigurationPanel = () => {
  const { globalVariables, localVariables } = useVariableContext();
  const { isModalVisible, closeModal } = useModalManager();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [initialData, setInitialData] = useState<VariableValue | undefined>(undefined);
  const [scope, setScope] = useState<VariableScope>("global");
  const { flowId } = useParams<{ flowId: string }>();
  const { deleteVariable } = useLocalVariables(flowId);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState<string>();

  const handleCreateVariable = (scope: VariableScope) => {
    setScope(scope);
    setCreateModalOpen(true);
  };

  const handleEditVariable = (variable: VariableValue) => {
    setInitialData(variable);
    setCreateModalOpen(true);
  };
  const handleDeleteLocalVariables = async () => {
    if (selectedIdToDelete) {
      try {
        await deleteVariable(selectedIdToDelete);
      } catch (e) {
        // catching the error here because DeleteDialog doesnt catch errors
        // not doing anything with the error because it is handled in the hook callback
        e;
      }
    }
  };

  const tabs: TabConfig[] = [
    {
      label: cloudflowTexts.GLOBAL_VARIABLES,
      content: <VariableTab variables={globalVariables} onCreateVariable={handleCreateVariable} scope="global" />,
      key: "global-variables",
    },
    {
      label: cloudflowTexts.LOCAL_VARIABLES,
      content: (
        <VariableTab
          variables={localVariables}
          onCreateVariable={handleCreateVariable}
          scope="local"
          onEditVariable={handleEditVariable}
          onDeleteVariable={(id) => {
            setSelectedIdToDelete(id);
          }}
        />
      ),
      key: "local-variables",
    },
  ];
  return (
    <SideDrawer
      open={isModalVisible("variableSidePanel")}
      onClose={() => {
        closeModal("variableSidePanel");
      }}
      variant="persistent"
      sideDrawerSx={{
        my: 6.6,
        borderRadius: 0,
        mx: 0,
        "& .MuiDivider-root": {
          marginBottom: 0,
        },
        height: (theme) => `calc(100vh - ${theme.spacing(7)})`,
      }}
      disableGutters={true}
      title={cloudflowTexts.VARIABLES}
    >
      <Stack
        sx={{
          height: "100%",
        }}
      >
        <Stack sx={{ p: 2, justifyContent: "center" }}>
          <Typography variant="subtitle2" color="text.primary" fontWeight={500}>
            {cloudflowTexts.VARIABLES_TAB_TITLE}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {cloudflowTexts.VARIABLES_TAB_DESCRIPTION}
          </Typography>

          <Tabs tabs={tabs} variant="fullWidth" />
        </Stack>

        <DeleteDialog
          open={!!selectedIdToDelete}
          title={cloudflowTexts.DELETE_VARIABLE}
          message={cloudflowTexts.DELETE_VARIABLE_MESSAGE}
          onDelete={handleDeleteLocalVariables}
          onClose={() => {
            setSelectedIdToDelete(undefined);
          }}
        />

        <CreateVariableModalWrapper
          createModalOpen={createModalOpen}
          setCreateModalOpen={setCreateModalOpen}
          initialData={initialData}
          setInitialData={setInitialData}
          scope={scope}
          flowId={flowId}
        />
      </Stack>
    </SideDrawer>
  );
};

export default VariableConfigurationPanel;
